import {
  Box,
  Dialog,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { BrandMatchResult } from "../constants";
import { formatAcquiredDate } from "./LeadsTable";

interface BrandMatchModalProps {
  data: LeadsManagementData;
}

// Loading-state skeleton counts mirror the mockup: 4 brand-card placeholders
// above one tall condition-table placeholder.
const BRAND_CARD_SKELETON_COUNT = 4;

function matchResultColor(result: BrandMatchResult): string {
  if (result === "Match") return "success.main";
  if (result === "No Match") return "error.main";
  return "grey.500";
}

export default function BrandMatchModal({ data }: BrandMatchModalProps) {
  const {
    matchModalOpen,
    matchLead,
    matchLoading,
    matchError,
    matchDiagnostic,
    closeBrandMatch,
    onMatchModalExited,
    rerunBrandMatch,
    canReportToBrand,
    reportToBrand,
    reportingBrandKey,
  } = data;

  const eligibleOtherBrands = matchDiagnostic
    ? matchDiagnostic.brands.filter((brand) => brand.eligible && !brand.assigned)
    : [];
  const hasEligibleOthers = eligibleOtherBrands.length > 0;
  const totalConditions = matchDiagnostic?.rows.length ?? 0;
  const brandColumnCount = matchDiagnostic?.brands.length ?? 0;
  const orderedBrands = matchDiagnostic
    ? [...matchDiagnostic.brands].sort((a, b) => Number(b.assigned) - Number(a.assigned))
    : [];

  return (
    <Dialog
      open={matchModalOpen}
      onClose={(_event, reason) => {
        // Only the header/footer close controls should dismiss this modal —
        // an accidental click outside or Escape shouldn't lose the query result.
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        closeBrandMatch();
      }}
      TransitionProps={{ onExited: onMatchModalExited }}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "92vh" } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, px: "24px", py: "16px"}}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 500, lineHeight: "32px",color: "text.primary" }}>
            Brand Match Diagnostic
          </Typography>
          {matchLead && (
            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: "166%",
                  letterSpacing: "0.4px",
                  color: "text.secondary",
                }}
              >
                Lead: #{matchLead.id}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: "166%",
                  letterSpacing: "0.4px",
                  color: "text.secondary",
                }}
              >
                Full Name: {matchLead.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: "166%",
                  letterSpacing: "0.4px",
                  color: "text.secondary",
                }}
              >
                Assigned To: {matchLead.brand}
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton size="medium" onClick={rerunBrandMatch} title="Re-run query">
          <RefreshIcon fontSize="medium" sx={{ color: "secondary.main" }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 3.25, pt: 0, pb: "16px" }}>
        {matchLoading && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.125,
                borderRadius: 2,
                px: "16px",
                py: "8px",
                fontSize: 14,
                lineHeight: "20px",
                fontWeight: 400,
                bgcolor: "#fbf7fd",
              }}
            >
              <Box
                sx={{
                  width: 19,
                  height: 19,
                  borderRadius: "50%",
                  border: "2.5px solid #ecdcf3",
                  borderTopColor: "secondary.main",
                  "@keyframes brandMatchSpin": { to: { transform: "rotate(360deg)" } },
                  animation: "brandMatchSpin 0.8s linear infinite",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 13.5, color: "#6A1B9A", whiteSpace: "nowrap" }}>
                Running brand match query for lead #{matchLead?.id}…
              </Typography>
              <Typography
                sx={{
                  ml: "auto",
                  fontSize: 12,
                  color: "#a98bb5",
                  fontFamily: "'Roboto Mono', monospace",
                }}
              >
                BQL · brand_match_diagnostic
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              {Array.from({ length: BRAND_CARD_SKELETON_COUNT }).map((_, index) => (
                <Skeleton key={index} variant="rounded" sx={{ flex: 1, height: 132, bgcolor: "#f1eef3" }} />
              ))}
            </Box>
            <Skeleton variant="rounded" sx={{ height: 290, mt: 2.25, bgcolor: "#f1eef3" }} />
          </Box>
        )}

        {!matchLoading && matchError && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, bgcolor: "#FFEBEE", border: "1px solid #f5c6cb", borderRadius: 2, px: 2, py: 1.75 }}>
            <InfoOutlinedIcon sx={{ color: "error.main", fontSize: 20 }} />
            <Typography sx={{ fontSize: 13.5, color: "error.main", flex: 1 }}>
              {matchError}
            </Typography>
            <Typography
              onClick={rerunBrandMatch}
              sx={{ fontSize: 12.5, fontWeight: 500, color: "secondary.main", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Re-run query
            </Typography>
          </Box>
        )}

        {!matchLoading && !matchError && matchDiagnostic && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.125,
                borderRadius: 2,
                px: "16px",
                py: "8px",
                fontSize: 14,
                lineHeight: "20px",
                fontWeight: 400,
                bgcolor: hasEligibleOthers ? "#F1F8F2" : "#E5F6FD",
                color: hasEligibleOthers ? "success.dark" : "#014361",
              }}
            >
              {hasEligibleOthers ? (
                <CheckCircleOutlinedIcon sx={{ fontSize: 18, color: "success.main" }} />
              ) : (
                <InfoOutlinedIcon sx={{ fontSize: 18, color: "info.main" }} />
              )}
              <Typography sx={{ fontSize: 13.5 }}>
                {hasEligibleOthers
                  ? `This lead meets every requirement of ${eligibleOtherBrands.length} additional brand${eligibleOtherBrands.length > 1 ? "s" : ""} beyond ${matchLead?.brand ?? "its assigned brand"}.`
                  : `No additional brand accepts this lead on all ${totalConditions} condition${totalConditions === 1 ? "" : "s"}.`}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              {orderedBrands.map((brand) => {
                const barPercent = brand.totalConditions
                  ? (brand.matchedCount / brand.totalConditions) * 100
                  : 0;
                const canReport = brand.eligible && !brand.assigned && canReportToBrand;
                const isReportingThisBrand = reportingBrandKey === brand.key;
                const reportDisabled = reportingBrandKey !== null && !isReportingThisBrand;
                return (
                  <Box
                    key={brand.key}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: "8px",
                      p: "8px",
                      border: `1px solid ${brand.assigned ? "#9C27B080" : "#ececec"}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: 14,
                          lineHeight: "143%",
                          letterSpacing: "0.15px",
                          verticalAlign: "middle",
                          color: "grey.900",
                        }}
                      >
                        {brand.name}
                      </Typography>
                      {brand.assigned && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            px: "4px",
                            py: "2px",
                            borderRadius: "4px",
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: 10,
                            lineHeight: "14px",
                            letterSpacing: 0,
                            bgcolor: "#9C27B014",
                            color: "secondary.dark",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Assigned
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {brand.eligible ? (
                          <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
                        ) : (
                          <CloseIcon sx={{ fontSize: 16, color: "error.main" }} />
                        )}
                        <Typography
                          sx={{
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: 12,
                            lineHeight: "166%",
                            letterSpacing: "0.4px",
                            color: brand.eligible ? "success.main" : "error.main",
                          }}
                        >
                          {brand.eligible ? "Eligible" : "Not Eligible"}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                        {brand.matchedCount}/{brand.totalConditions}
                      </Typography>
                    </Box>
                    <Box sx={{ height: 5, borderRadius: 3, bgcolor: "grey.100", mt: 1, overflow: "hidden" }}>
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: 3,
                          width: `${barPercent}%`,
                          bgcolor: brand.eligible ? "success.main" : "error.main",
                        }}
                      />
                    </Box>
                    {(brand.assigned || !brand.eligible) && (
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: 12,
                          lineHeight: "166%",
                          letterSpacing: "0.4px",
                          color: "text.secondary",
                          mt: 0.875,
                        }}
                      >
                        {brand.assigned
                          ? `Assigned on :${matchLead ? formatAcquiredDate(matchLead.date) : ""}`
                          : "Conditions not met."}
                      </Typography>
                    )}
                    {canReport && (
                      <Typography
                        onClick={() => !reportDisabled && !isReportingThisBrand && reportToBrand(brand.key)}
                        data-tracker="1037"
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: 12,
                          lineHeight: "166%",
                          letterSpacing: "0.4px",
                          color: reportDisabled ? "text.disabled" : "#0000008F",
                          cursor: reportDisabled || isReportingThisBrand ? "default" : "pointer",
                          textDecoration: isReportingThisBrand ? "none" : "underline",
                          textDecorationStyle: "solid",
                          mt: 0.875,
                        }}
                      >
                        {isReportingThisBrand ? "Reporting…" : "Report to this brand"}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ mt: 2.25, overflowX: "auto" }}>
              <Box sx={{ minWidth: 1000 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `190px 150px repeat(${brandColumnCount}, minmax(140px, 1fr))`,
                    borderBottom: "1px solid #ececec",
                  }}
                >
                  <Box sx={{ px: 1.75, py: 1.25, fontSize: 14, lineHeight: "14px", fontWeight: 500, color: "text.primary" }}>
                    Condition
                  </Box>
                  <Box sx={{ px: 1.5, py: 1.25, fontSize: 14, lineHeight: "14px", fontWeight: 500, color: "text.primary" }}>
                    Lead Answer
                  </Box>
                  {orderedBrands.map((brand) => (
                    <Box
                      key={brand.key}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        fontSize: 14,
                        lineHeight: "14px",
                        fontWeight: 500,
                        color: brand.assigned ? "secondary.main" : "text.primary",
                      }}
                    >
                      {brand.name}
                    </Box>
                  ))}
                </Box>
                {matchDiagnostic.rows.map((row) => (
                  <Box
                    key={row.key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `190px 150px repeat(${brandColumnCount}, minmax(140px, 1fr))`,
                      borderBottom: "1px solid #f4f4f4",
                    }}
                  >
                    <Box sx={{ px: 1.75, py: 1.375 }}>
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: 14,
                          lineHeight: "143%",
                          letterSpacing: "0.17px",
                          color: "text.primary",
                        }}
                      >
                        {row.nice}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 400,
                          fontSize: 12,
                          lineHeight: "166%",
                          letterSpacing: "0.4px",
                          color: "text.secondary",
                        }}
                      >
                        {row.key}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 1.5, py: 1.375, fontSize: 14, color: "text.primary" }}>
                      {row.leadAnswer}
                    </Box>
                    {orderedBrands.map((brand) => {
                      const cell = row.cells.find((c) => c.brandKey === brand.key);
                      return (
                        <Box key={brand.key} sx={{ px: 1.5, py: 1.375 }}>
                          {cell && (
                            <>
                              <Typography sx={{ fontSize: 14, color: "text.primary" }}>{cell.requirement}</Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Roboto",
                                  fontWeight: 400,
                                  fontSize: 12,
                                  lineHeight: "20px",
                                  letterSpacing: "0.4px",
                                  color: matchResultColor(cell.matchResult),
                                }}
                              >
                                {cell.matchResult}
                              </Typography>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: "8px", pb: "16px", pl: "24px", pr: "24px" }}>
        <Box
          component="button"
          onClick={closeBrandMatch}
          sx={{
            border: "none",
            bgcolor: "secondary.main",
            color: "common.white",
            fontFamily: "'Roboto', sans-serif",
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: "0.4px",
            px: 3,
            py: 1.375,
            borderRadius: 1,
            cursor: "pointer",
            "&:hover": { bgcolor: "secondary.dark" },
          }}
        >
          Close
        </Box>
      </Box>
    </Dialog>
  );
}
